<template>
  <div
    v-loading="isLoading || isSubmitting"
    class="cloud-intercom-page"
  >
    <div class="ci-body">
      <h1 class="ci-body__title">
        {{ $t('Intercom.CloudIntercom.RegistrationPage.Title') }}
      </h1>
      <form @submit.prevent>
        <div class="ci-form">
          <label class="ci-form__label">
            {{ $t('Intercom.CloudIntercom.RegistrationPage.GuestName') }}<span class="ci-form__required">*</span>
          </label>
          <input
            v-model="formData.guestName"
            class="ci-form__input"
            :class="{ 'ci-form__input--has-value': formData.guestName }"
            type="text"
            placeholder="請輸入"
            maxlength="20"
          >
        </div>
        <div class="ci-form">
          <label class="ci-form__label">
            {{ $t('Intercom.CloudIntercom.RegistrationPage.Cause') }}<span class="ci-form__required">*</span>
          </label>
          <select
            v-model="formData.cause"
            class="ci-form__select"
            :class="{ 'ci-form__input--has-value': !!formData.cause }"
          >
            <option
              :value="0"
              disabled
              hidden
            >
              {{ $t('Intercom.CloudIntercom.RegistrationPage.SelectPlaceholder') }}
            </option>
            <option
              v-for="item in causeList"
              :key="item.Id"
              :value="item.Id"
            >
              {{ item.Name }}
            </option>
          </select>
        </div>
        <div class="ci-form">
          <label class="ci-form__label">
            {{ $t('Intercom.CloudIntercom.RegistrationPage.Unit') }}<span class="ci-form__required">*</span>
          </label>
          <select
            v-model="selectedDongIndex"
            class="ci-form__select"
            :class="{ 'ci-form__input--has-value': selectedDongIndex !== -1 }"
            @change="selectDong"
          >
            <option
              :value="-1"
              disabled
              hidden
            >
              {{ $t('Intercom.CloudIntercom.RegistrationPage.SelectPlaceholder') }}
            </option>
            <option
              v-for="(item, index) in tabletList"
              :key="index"
              :value="index"
            >
              {{ item.Tag }}
            </option>
          </select>
          <select
            v-if="selectedDongIndex >= 0"
            v-model="selectedFloorIndex"
            class="ci-form__select"
            :class="{ 'ci-form__input--has-value': selectedFloorIndex !== -1 }"
            :disabled="tabletList[selectedDongIndex].FloorAndTablets.length === 1"
            @change="selectFloor"
          >
            <option
              :value="-1"
              disabled
              hidden
            >
              {{ $t('Intercom.CloudIntercom.RegistrationPage.SelectFloor') }}
            </option>
            <option
              v-for="(item, index) in tabletList[selectedDongIndex].FloorAndTablets"
              :key="index"
              :value="index"
            >
              {{ $t('Intercom.CloudIntercom.RegistrationPage.FloorLabel', { floor: item.Floor }) }}
            </option>
          </select>
          <select
            v-else
            class="ci-form__select"
            disabled
          >
            <option value="">
              {{ $t('Intercom.CloudIntercom.RegistrationPage.SelectFloor') }}
            </option>
          </select>
          <select
            v-if="selectedFloorIndex >= 0"
            v-model="formData.tabletId"
            class="ci-form__select"
            :class="{ 'ci-form__input--has-value': formData.tabletId }"
            :disabled="tabletList[selectedDongIndex].FloorAndTablets[selectedFloorIndex].Tablets.length === 1"
          >
            <option
              value=""
              disabled
              hidden
            >
              {{ $t('Intercom.CloudIntercom.RegistrationPage.SelectUnit') }}
            </option>
            <option
              v-for="item in tabletList[selectedDongIndex].FloorAndTablets[selectedFloorIndex].Tablets"
              :key="item.Id"
              :value="item.Id"
            >
              {{ item.Name }}
            </option>
          </select>
          <select
            v-else
            class="ci-form__select"
            disabled
          >
            <option value="">
              {{ $t('Intercom.CloudIntercom.RegistrationPage.SelectUnit') }}
            </option>
          </select>
        </div>
      </form>
    </div>
    <div class="ci-footer">
      <button
        class="ci-btn ci-btn--primary submit"
        :disabled="!isFormReady || isSubmitting"
        @click="submit"
      >
        {{ $t('Intercom.CloudIntercom.RegistrationPage.Submit') }}
      </button>
      <!-- reCAPTCHA 免責聲明（徽章已隱藏，依 Google 政策須顯示此文字） -->
      <p class="ci-recaptcha-disclaimer">
        This site is protected by reCAPTCHA and the Google
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        and
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        apply.
      </p>
    </div>
  </div>
</template>

<script>
import { apiVoip } from '@/api/Voip'

export default {
  name: 'RegistrationPage',
  props: {
    geolocationToken: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      // 表單資料載入中（GetOnSiteRegisterFormInfo）
      isLoading: false,
      // 送出中（CreateAndArrived），防止重複提交
      isSubmitting: false,
      // 訪客事由清單，由 GetOnSiteRegisterFormInfo 回傳
      causeList: [],
      // 拜訪戶別的棟別結構，由 GetOnSiteRegisterFormInfo 回傳
      tabletList: [],
      // 目前選擇的棟別索引（-1 = 未選擇）
      selectedDongIndex: -1,
      // 目前選擇的樓層索引（-1 = 未選擇）
      selectedFloorIndex: -1,
      formData: {
        // 訪客/廠商名稱
        guestName: '',
        // 訪客事由 ID（0 為未選擇）
        cause: 0,
        // 選定的門口機 Tablet ID
        tabletId: ''
      }
    }
  },
  computed: {
    /** 三個必填欄位都有值才允許送出 */
    isFormReady () {
      return this.formData.guestName !== '' &&
        this.formData.cause !== 0 &&
        this.formData.tabletId !== ''
    }
  },
  async mounted () {
    await this.fetchFormData()
  },
  methods: {
    /**
     * 載入表單所需的靜態資料：訪客事由清單、棟別結構、社區名稱
     */
    async fetchFormData () {
      this.isLoading = true
      try {
        const res = await apiVoip.Guest.GetOnSiteRegisterFormInfo({
          GeolocationToken: this.geolocationToken
        })
        this.causeList = res.data.CauseTypes
        this.tabletList = res.data.StructuredTablets
        this.$emit('community-name', res.data.CommunityInfo.Name)
      } catch {
        // 讀取失敗時表單保持空白
      } finally {
        this.isLoading = false
      }
    },

    /**
     * 棟別變更時重置樓層與戶別，若該棟只有一個樓層則自動選定
     */
    selectDong () {
      this.formData.tabletId = ''
      this.selectedFloorIndex = -1
      if (this.tabletList[this.selectedDongIndex].FloorAndTablets.length === 1) {
        this.selectedFloorIndex = 0
        this.selectFloor()
      }
    },

    /**
     * 樓層變更時重置戶別，若該樓層只有一個戶別則自動選定
     */
    selectFloor () {
      this.formData.tabletId = ''
      const tablets = this.tabletList[this.selectedDongIndex].FloorAndTablets[this.selectedFloorIndex].Tablets
      if (tablets.length === 1) {
        this.formData.tabletId = tablets[0].Id
      }
    },

    /**
     * 送出登記表單，呼叫 CreateAndArrived 取得 guestToken 並 emit 給父層
     */
    async submit () {
      if (this.isSubmitting) return
      this.isSubmitting = true
      try {
        await this.$recaptchaLoaded()
        const recaptchaToken = await this.$recaptcha('guest_arrival_on_site')
        const res = await apiVoip.Guest.CreateAndArrived({
          GeolocationToken: this.geolocationToken,
          GuestName: this.formData.guestName,
          Cause: this.formData.cause,
          TabletId: this.formData.tabletId,
          RecaptchaToken: recaptchaToken
        })
        this.$message({ type: 'success', message: this.$t('Intercom.CloudIntercom.RegistrationPage.SuccessMessage') })
        this.$emit('arrived-by-form', { guestToken: res.data.GuestToken })
      } catch {
        this.$alert(this.$t('Intercom.CloudIntercom.RegistrationPage.ErrorMessage'), this.$t('Intercom.CloudIntercom.RegistrationPage.ErrorTitle'), {
          confirmButtonText: this.$t('Intercom.CloudIntercom.Common.Confirm'),
          type: 'error',
          showClose: false,
          roundButton: true,
          closeOnClickModal: false,
          customClass: 'ci-alert'
        }).catch(() => {})
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import './cloudIntercom';

.ci-body {
  & form {
    width: 100%;
  }
}

.ci-form {
  width: 100%;
  &:not(:first-child) {
    margin-top: 0;
  }
}

.ci-footer {
  margin-top: 0;
  display: flex;
  flex-direction: column;
}
</style>
